import React, {PropsWithChildren} from 'react';
import Flatpickr from "react-flatpickr";
import SvgIcon from '../../Components/Icons/SvgIcon';
import Calendar from '../../Components/Icons/Calendar';
import Button from '../../Components/Button';
import {addDays, formatDate, isDateSearchable, subtractDays} from '../../Foundations/Utils/DateUtil';
import ArrowDown from '../../Components/Icons/ArrowDown';

interface IProps {
    onSelected(result: Date): void;
    onDateNavSelected?(selected: Date): void;
    selectedDate: Date | null;
    enableDateNav?: boolean;
    maxDate: Date;
}

const getAdjacentDateFromProps = (selectedDate: Date | null, isPrevDay: boolean = false): Date | null => {
    if (selectedDate === null) {
        return null;
    }

    const adjacentDate: Date = isPrevDay === true
        ? addDays(selectedDate, 1)
        : subtractDays(selectedDate, 1);

    return isDateSearchable(adjacentDate) === true ? adjacentDate : null;
}

const StartDatePicker: React.FC<IProps> = (props: PropsWithChildren<IProps>) => {
    const nextDay: Date | null = getAdjacentDateFromProps(props.selectedDate);
    const prevDay: Date | null = getAdjacentDateFromProps(props.selectedDate, true);

    const handleDateNavChange = (date: Date | null): void => {
        if (date === null || typeof props.onDateNavSelected === 'undefined') {
            return;
        }

        props.onDateNavSelected(date);
    }

    return (
        <div className="start-date-picker flex relative rounded shadow shadow-2xl bg-white">
            {
                props.enableDateNav === true &&
                <Button onClick={() => handleDateNavChange(nextDay)}
                        text="next day"
                        width="w-3/12 border-r border-1 border-gray-300"
                        bg="bg-transparent"
                        justify="justify-start"
                        rounded="rounded-">
                    {
                        nextDay !== null &&
                        <SvgIcon className="fill-gray-600 transform rotate-90">
                            <ArrowDown />
                        </SvgIcon>
                    }
                    <span className="text-gray-600 text-sm">
                        {nextDay !== null && formatDate(nextDay, 'dd MMM')}
                    </span>
                </Button>
            }
            <div className={`relative ${props.enableDateNav === true ? 'w-6/12 px-2' : ''}`}>
                <SvgIcon className="absolute fill-orange m-2">
                    <Calendar />
                </SvgIcon>
                <Flatpickr
                    value={props.selectedDate || props.maxDate}
                    options={{
                        maxDate: props.maxDate,
                        enableTime: false,
                        altInputClass: 'pl-10 h-10 w-full font-sans font-bold text-gray-600 py-2 outline-none block rounded',
                        altFormat: 'd M Y',
                        altInput: true,
                        clickOpens: props.enableDateNav !== true
                    }}
                    onChange={(selectedDate: Date) => props.onSelected(selectedDate[0])}
                />
            </div>
            {
                props.enableDateNav === true &&
                <Button onClick={() => handleDateNavChange(prevDay)}
                        text="prev day"
                        width="w-3/12 border-l border-1 border-gray-300"
                        justify="justify-end"
                        bg="bg-transparent"
                >
                    <span className="text-gray-600 text-sm">
                        {prevDay !== null && formatDate(prevDay, 'dd MMM')}
                    </span>
                    {
                        prevDay !== null &&
                        <SvgIcon className="fill-gray-600 transform -rotate-90">
                            <ArrowDown />
                        </SvgIcon>
                    }
                </Button>
            }
        </div>
    )
}

export default StartDatePicker;
